import { useCallback, useReducer } from 'react'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { ApiStates } from './constants'

const initialState = {
  isLoading: false,
  errorMessage: '',
  hasError: false,
  data: undefined,
}

type ActionTypes<T> =
  | { type: ApiStates.LOADING }
  | { type: ApiStates.ERROR; payload: string }
  | { type: ApiStates.SUCCESS; payload: T }
  | { type: ApiStates.UPDATE; payload: T }

interface ICallApiState<T> {
  isLoading: boolean
  hasError: boolean
  errorMessage: string
  data?: T
}

function callApiReducer<T>(state: ICallApiState<T> = initialState, action: ActionTypes<any>) {
  switch (action.type) {
    case ApiStates.LOADING: {
      return {
        ...initialState,
        isLoading: true,
      }
    }
    case ApiStates.SUCCESS: {
      return {
        ...initialState,
        data: action.payload,
      }
    }
    case ApiStates.UPDATE: {
      return {
        ...initialState,
        data: {
          ...state.data,
          ...action.payload,
        },
      }
    }
    case ApiStates.ERROR: {
      return {
        ...initialState,
        hasError: true,
        errorMessage: action.payload,
      }
    }
    default: {
      return state
    }
  }
}

export interface ICallApi<T, R> extends ICallApiState<R> {
  load: (resp: T) => Promise<R>
  update: (payload: R) => void
}

interface IUseCallApiParams<T, R> {
  apiCall: (resp: T) => Promise<R>
  message?: string
  showSuccess?: boolean
}

/**
 * Simple api calls
 *
 * @param apiCall Any api call function
 * @param message Optional success message override
 * @param showSuccess Optional display/hide success message, default to false
 *
 * @example
 *  const {
 *    load,
 *    data,
 *    isLoading,
 *  } = useCallApi({ apiCall: getShipmentDetail })
 *
 * */
export const useCallApi = <T, R>({
  apiCall,
  message = 'Changes Saved!',
  showSuccess = false,
}: IUseCallApiParams<T, R>): ICallApi<T, R> => {
  const { openToast } = useGlobalToast()
  const [{ data, errorMessage, hasError, isLoading }, dispatchState] = useReducer(
    callApiReducer,
    initialState
  )

  const load = useCallback(
    async payload => {
      dispatchState({ type: ApiStates.LOADING })

      try {
        const response = await apiCall(payload)
        dispatchState({ type: ApiStates.SUCCESS, payload: response })
        if (showSuccess) {
          openToast({
            toastMessage: message,
            toastType: 'default',
          })
        }
        return response
      } catch (err) {
        dispatchState({ type: ApiStates.ERROR, payload: err })
        throw Error(err)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiCall, openToast, dispatchState, message]
  )

  const update = (payload: R) => dispatchState({ type: ApiStates.UPDATE, payload })

  return {
    load,
    data,
    update,
    isLoading,
    hasError,
    errorMessage,
  }
}
